import {
  ConfirmDialog,
} from "@common";
import {
  activityRowSelect,
  getActivityApi,
  getProjectUser,
} from "@features";
import { Box } from "@mui/material";
import { KanbanDetailsContent } from "@pionglobal/pionsharedui";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ActivityEdit from "./ActivityEdit";
import useActivityForm from "./hooks/useActivityForm";
import { getActivityView } from "./utils/activityView";

const SEGMENT = {
  ACTIVITY: "activity_details",
};

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const resolveActivityRecord = (activityData, selectedActivity, activityId) => {
  if (Array.isArray(activityData)) {
    const found = activityData.find((item) => item?.id === activityId);
    if (found) return found;
  } else if (activityData?.id === activityId) {
    return activityData;
  }
  if (Array.isArray(selectedActivity)) {
    return selectedActivity[0] || {};
  }
  return selectedActivity || {};
};

const updateSelectedActivityItem = (res, activityId, dispatch) => {
  const list = res?.payload?.data || res?.payload || [];
  if (!Array.isArray(list) || !activityId) return;
  const updatedItem = list.find((item) => item?.id === activityId);
  if (updatedItem) {
    dispatch(activityRowSelect([updatedItem]));
  }
};

const isDeleteSuccessful = (res) =>
  Boolean(
    res?.payload?.ok ||
    res?.payload?.data?.ok ||
    res?.meta?.requestStatus === "fulfilled" ||
    !res?.error
  );

export default function ActivityView({
  activityId: propActivityId,
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forContact,
  forContract,
  forOrder,
  forCustomer,
  campaignId: propCampaignId,
  prospectId: propProspectId,
  inquiryId: propInquiryId,
  leadId: propLeadId,
  opportunityId: propOpportunityId,
  customerAccountId: propCustomerAccountId,
  contactId: propContactId,
  contractId: propContractId,
  orderId: propOrderId,
  customerId: propCustomerId,
  getFunction: parentGetFunction,
  heading = "Activity",
  permissions = {},
  ...props
}) {
  const dispatch = useDispatch();

  const campaignSelectedData = useSelector((state) => state.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state.account?.selectedData);
  const contactSelectedData = useSelector((state) => state.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state.order?.selectedData);
  const customerSelectedData = useSelector((state) => state.customer?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forContact,
    forContract,
    forOrder,
    forCustomer,
    campaignId: propCampaignId,
    prospectId: propProspectId,
    inquiryId: propInquiryId,
    leadId: propLeadId,
    opportunityId: propOpportunityId,
    customerAccountId: propCustomerAccountId,
    contactId: propContactId,
    contractId: propContractId,
    orderId: propOrderId,
    customerId: propCustomerId,
    ...props,
  };

  const api = useMemo(
    () => getActivityApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forContact,
      forContract,
      forOrder,
      forCustomer,
      propCampaignId,
      propProspectId,
      propInquiryId,
      propLeadId,
      propOpportunityId,
      propCustomerAccountId,
      propContactId,
      propContractId,
      propOrderId,
      propCustomerId,
      props?.customerId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const rawPropId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (rawPropId && (typeof rawPropId === "string" || typeof rawPropId === "number")) {
      return rawPropId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(propContractId) || getSelectedId(contractSelectedData);
        break;
      case "customer-account":
        resolved = sanitizeId(propCustomerAccountId) || getSelectedId(accountSelectedData);
        break;
      case "contact":
        resolved = sanitizeId(propContactId) || getSelectedId(contactSelectedData);
        break;
      case "opportunity":
        resolved = sanitizeId(propOpportunityId) || getSelectedId(opportunitySelectedData);
        break;
      case "lead":
        resolved = sanitizeId(propLeadId) || getSelectedId(leadSelectedData);
        break;
      case "campaign":
        resolved = sanitizeId(propCampaignId) || getSelectedId(campaignSelectedData);
        break;
      case "inquiry":
        resolved = sanitizeId(propInquiryId) || getSelectedId(inquirySelectedData);
        break;
      case "prospect":
        resolved = sanitizeId(propProspectId) || getSelectedId(prospectSelectedData);
        break;
      case "customer":
        resolved = sanitizeId(propCustomerId) || sanitizeId(props?.customerId) || getSelectedId(customerSelectedData);
        break;
      case "order":
        resolved = sanitizeId(propOrderId) || getSelectedId(orderSelectedData);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    propContractId,
    propCustomerAccountId,
    propContactId,
    propOpportunityId,
    propLeadId,
    propCampaignId,
    propInquiryId,
    propProspectId,
    propOrderId,
    propCustomerId,
    props?.customerId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
  ]);

  const selectedActivity = useSelector((state) => state.activity?.selectedData);
  const activityState = useSelector((state) => state.activity);
  const activityData = activityState?.data;
  const projectUsersOptions = useSelector((state) => state?.projectUsers?.data) || [];

  const loading = activityState?.loading;
  const [editingSegment, setEditingSegment] = useState(null);

  const activeId = propActivityId || getSelectedId(selectedActivity);

  const record = useMemo(() => {
    return resolveActivityRecord(activityData, selectedActivity, activeId);
  }, [activityData, selectedActivity, activeId]);

  const refreshList = useCallback(() => {
    const cleanEntityId = sanitizeId(activeEntityId);
    if (cleanEntityId) {
      dispatch(api.get({ [api.idKey]: cleanEntityId })).then((res) => {
        updateSelectedActivityItem(res, activeId, dispatch);
      });
    } else {
      dispatch(api.get()).then((res) => {
        updateSelectedActivityItem(res, activeId, dispatch);
      });
    }
    dispatch(getProjectUser());
    parentGetFunction?.();
  }, [dispatch, api, activeEntityId, activeId, parentGetFunction]);

  const handleSuccess = useCallback(() => {
    setEditingSegment(null);
    refreshList();
  }, [refreshList]);

  const activityForm = useActivityForm({
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forContact,
    forContract,
    forOrder,
    forCustomer,
    entityType: api.entityType,
    ...(sanitizeId(activeEntityId) ? { [api.idKey]: sanitizeId(activeEntityId) } : {}),
    data: record,
    onSuccess: handleSuccess,
  });

  const activityView = useMemo(
    () => getActivityView(record, projectUsersOptions),
    [record, projectUsersOptions]
  );

  const canOpenSegment = useCallback(
    (segmentId) => editingSegment === null || editingSegment === segmentId,
    [editingSegment]
  );

  const openSegment = useCallback((segmentId, formik) => {
    formik?.resetForm?.();
    setEditingSegment(segmentId);
  }, []);

  const cancelSegment = useCallback((formik) => {
    formik?.resetForm?.();
    setEditingSegment(null);
  }, []);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const handleDelete = useCallback(() => {
    const idToDelete = activeId || record?.id;
    if (!idToDelete) return;
    dispatch(api.delete({ id: idToDelete })).then((res) => {
      if (isDeleteSuccessful(res)) {
        setConfirmDialog({ isOpen: false, title: "", subTitle: "" });
        dispatch(activityRowSelect([]));
        const cleanEntityId = sanitizeId(activeEntityId);
        if (cleanEntityId) {
          dispatch(api.get({ [api.idKey]: cleanEntityId }));
        } else {
          dispatch(api.get());
        }
        dispatch(getProjectUser());
        parentGetFunction?.();
      }
    });
  }, [dispatch, api, activeId, record?.id, activeEntityId, parentGetFunction]);

  const handleDeleteClick = useCallback(() => {
    setConfirmDialog({
      isOpen: true,
      title: "Are you sure to delete this record?",
      subTitle: "You can't undo this operation",
      onConfirm: handleDelete,
    });
  }, [handleDelete]);

  const segments = useMemo(
    () => [
      {
        id: SEGMENT.ACTIVITY,
        visible: true,
        gridSize: { xs: 12, sm: 12, md: 12, lg: 12 },
        title: heading ? `${heading} Details` : "Activity Details",
        left: activityView.left,
        right: activityView.right,
        fullWidth: activityView.fullWidth,
        editable: canOpenSegment(SEGMENT.ACTIVITY) && permissions?.update,
        isEditing: editingSegment === SEGMENT.ACTIVITY,
        editContent: (
          <ActivityEdit
            formik={activityForm.formik}
            options={activityForm.options}
            disabled={loading || activityForm.formik.isSubmitting}
          />
        ),
        onEdit: () => openSegment(SEGMENT.ACTIVITY, activityForm.formik),
        onSave: () => activityForm.formik.submitForm(),
        onCancel: () => cancelSegment(activityForm.formik),
        deleteAction: {
          visible: permissions?.delete,
          disabled: false,
          tooltip: "Delete Activity",
          onClick: handleDeleteClick,
        },
        close: {
          visible: true,
          disabled: false,
          onClick: () => {
            dispatch(activityRowSelect([]));
          },
        },
      },
    ],
    [
      record,
      activityView,
      canOpenSegment,
      editingSegment,
      activityForm,
      loading,
      openSegment,
      cancelSegment,
      handleDeleteClick,
      dispatch,
      heading,
      permissions,
    ]
  );

  const { user_created, user_updated, createdOn, updatedOn } = record || {};

  return (
    <Box sx={{ width: "100%", p: 0 }}>
      <KanbanDetailsContent segments={segments} />

      {(user_created || user_updated || createdOn || updatedOn) && (
        <Box sx={{ px: 2, pt: 1, pb: 1 }}>
        </Box>
      )}

      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Box>
  );
}
